import {
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Truck,
  CreditCard,
} from "lucide-react";

// Map issue status to Bootstrap badge classes
export const mapStatusToBootstrap = (status) => {
  switch (status?.toUpperCase()) {
    case "OPEN":
      return "badge bg-primary-subtle text-primary";
    case "CLOSED":
      return "badge bg-success-subtle text-success";
    case "PROCESSING":
      return "badge bg-info-subtle text-info";
    case "RESOLVED":
      return "badge bg-success-subtle text-success";
    case "INFO_REQUESTED":
    case "INFO_PROVIDED":
    case "INFO_NOT_AVAILABLE":
      return "badge bg-warning-subtle text-warning";
    case "RESOLUTION_PROPOSED":
    case "RESOLUTION_CASCADED":
    case "RESOLUTION_ACCEPTED":
    case "RESOLUTION_REJECTED":
      return "badge bg-info-subtle text-info";
    case "ESCALATED":
    case "CASCADED":
      return "badge bg-danger-subtle text-danger";
    default:
      return "badge bg-secondary-subtle text-secondary";
  }
};

// Map issue status to icons
export const getStatusIcon = (status) => {
  switch (status?.toUpperCase()) {
    case "OPEN":
    case "PROCESSING":
    case "RESOLUTION_PROPOSED":
      return <Clock size={14} />;
    case "CLOSED":
    case "RESOLVED":
    case "RESOLUTION_ACCEPTED":
      return <CheckCircle size={14} />;
    case "INFO_REQUESTED":
    case "INFO_PROVIDED":
    case "INFO_NOT_AVAILABLE":
    case "RESOLUTION_REJECTED":
      return <AlertTriangle size={14} />;
    case "ESCALATED":
    case "CASCADED":
      return <XCircle size={14} />;
    default:
      return <Clock size={14} />;
  }
};

// Map issue status/type to timeline dot colors
export const mapStatusToDotColor = (status) => {
  switch (status?.toUpperCase()) {
    case "OPEN":
      return "#0d6efd"; // bootstrap primary
    case "CLOSED":
      case "COMPLETED":
      return "#198754"; // bootstrap success
    case "PROCESSING":
      return "#0dcaf0"; // bootstrap info
    case "RESOLVED":
      return "#198754"; // bootstrap success (matches badge)
    case "INFO_REQUESTED":
    case "INFO_PROVIDED":
    case "INFO_NOT_AVAILABLE":
      return "#ffc107"; // bootstrap warning
    case "RESOLUTION_PROPOSED":
    case "RESOLUTION_CASCADED":
    case "RESOLUTION_ACCEPTED":
    case "RESOLUTION_REJECTED":
      return "#0dcaf0"; // bootstrap info
    case "ESCALATED":
    case "CASCADED":
      return "#dc3545"; // bootstrap danger
    default:
      return "#6c757d"; // gray fallback
  }
};

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  try {
    // FIX: Replace space with T to make it ISO compatible for all browsers
    // "2026-01-09 15:07:40" -> "2026-01-09T15:07:40"
    const safeDateString = dateString.replace(" ", "T");

    // Output: Jan 09, 2026 3:07 PM
    return format(new Date(safeDateString), "MMM dd, yyyy h:mm a");
  } catch (error) {
    //console.error("Date Error:", error);
    return dateString; // Return full string if error, don't chop it
  }
};

// Other utility functions stay the same
export const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);



export const formatDateTime = (date) => {
  if (!date) return "N/A";

  const d = new Date(date);

  if (isNaN(d.getTime())) return "N/A";

  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDuration = (duration) => {
  if (!duration) return "N/A";
  if (duration.includes("H")) {
    const hours = duration.match(/(\d+)H/)?.[1];
    return hours ? `${hours} Hour${hours > 1 ? "s" : ""}` : "N/A";
  }
  if (duration.includes("D")) {
    const days = duration.match(/(\d+)D/)?.[1];
    return days ? `${days} Day${days > 1 ? "s" : ""}` : "N/A";
  }
  return duration;
};

// Map action codes to labels and styling
export const actionMap = {
  OPEN: {
    type: "COMPLAINT OPENED",
    class: "complainant-action",
    defaultName: "Complainant",
    defaultContact: "N/A",
  },
  PROCESSING: {
    type: "PROCESSING",
    class: "respondent-action",
    defaultName: "Seller Support",
    defaultContact: "N/A",
  },
  INFO_REQUEST: {
    type: "INFO REQUESTED",
    class: "info-request",
    defaultName: "Support Team",
    defaultContact: "system",
  },
  RESOLVED: {
    type: "RESOLVED",
    class: "respondent-action",
    defaultName: "Support Team",
    defaultContact: "system",
  },
};

export const mapNotificationTypeToColor = (type) => {
  switch (type) {
    case "INFO_REQUESTED":
      return "bg-blue-100 text-blue-800";
    case "RESOLUTION_PROVIDED":
      return "bg-green-100 text-green-800";
    case "PENDING_ACTION":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const formatLabel = (value = "") => {
  if (!value) return "-";
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};



export const CATALOGUE_REJECTION = {
  "90001": { message: "Fulfilment rate below declared standards" },
  "90002": { message: "On-time delivery rate below declared standards" },
  "90003": { message: "Select call failure more than published threshold" },
  "90004": { message: "Init call failure more than published threshold" },
  "90005": { message: "Confirm call failure more than published threshold" },
  "90006": { message: "Percentage of customer issues resolved within resolution TATs is below published standards" },
  "90007": { message: "Duplicate Store" },
  "90008": { message: "Customer complaints more than published standards" },
  "90009": { message: "Rejected store due to Item level rejection above threshold" },
  "90010": { message: "Missing authorisation from NP" },
  "90011": { message: "Merchant status sent by Seller NP is unavailable" },
  "90012": { message: "Country of Origin not present (For Packaged Products)" },
  "90013": { message: "Only Hyperlocal Delivery allowed" },
  "90014": { message: "Invalid Latitude" },
  "90015": { message: "Invalid Longitude" },
  "90016": { message: "Invalid customisation group data" },
  "90017": { message: "Stores less than declared threshold" },
  "90018": { message: "Empty Address field" },
  "90019": { message: "Other Provider Error" },
  "90020": { message: "Tags type not present against items" },
  "90021": { message: "Invalid Address received" },
  "90022": { message: "Invalid city received" },
  "90023": { message: "Invalid contact number- not 10 digit" },
  "90024": { message: "Invalid FSSAI" },
  "90025": { message: "Delivery radius not given" },
  "90026": { message: "Store timing not in correct format" },
  "90027": { message: "Locality & city mismatch" },
  "90028": { message: "Store not received on search for 2 consecutive days" },
  "90029": { message: "Store disabled by Seller App" },
  "90030": { message: "Store Category mapping sent empty or unavailable at Buyer App side" },
  "90031": { message: "NP integration version mis-match" },
  "90032": { message: "Store location error" },
  "90033": { message: "Provider in Blacklisted" },
  "91001": { message: "Item price > MRP" },
  "91002": { message: "Use of restrictive keywords" },
  "91003": { message: "No Images found" },
  "91004": { message: "Duplicate Images Found" },
  "91005": { message: "Low quality Image Found" },
  "91006": { message: "Image Title Mismatch" },
  "91007": { message: "Other Mandatory/Statutory Field not there" },
  "91008": { message: "Duplicate Product" },
  "91010": { message: "Price is Zero" },
  "91011": { message: "Incorrect Image File Format" },
  "91012": { message: "Country of Origin not present (For Packaged Products)" },
  "91013": { message: "Net Quantity not present" },
  "91014": { message: "FSSAI/Statutory requirement not present (For packaged products)" },
  "91015": { message: "BIS Mark not present" },
  "91016": { message: "Product miscategorized" },
  "91017": { message: "Others - Add specific Error Description" },
  "91018": { message: "Available quantity for the SKU is either absent or invalid" },
  "91019": { message: "Category for the SKU does not exist" },
  "91020": { message: "The parameters for customisation of SKU are not shared" },
  "91021": { message: "Category field is missing" },
  "91022": { message: "Customer care contact details missing" },
  "91023": { message: "MRP less than zero" },
  "91024": { message: "Price less than zero" },
  "91025": { message: "Item status sent by Seller NP is unavailable" },
  "91026": { message: "Invalid Item ID" },
  "91027": { message: "Invalid Service Provider ID" },
  "91028": { message: "Customisation not present" },
  "91029": { message: "Invalid customisation group" },
  "92001": { message: "NP integration version mis-match" },
  "92002": { message: "Missing authorisation from NP" },
  "92003": { message: "Mandatory field missing or the data format is incorrect for all Providers" },
  "92004": { message: "Invalid domain sent" },
  "92005": { message: "NP is blacklisted or inactive or not onboarded yet" },
  "92006": { message: "Provider Rejections crossed threshold in single transaction ID" }
};